import { useState } from 'react';
import { useMutation } from 'react-relay';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, LoaderCircle, Mail, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { graphql } from 'react-relay';
import registerMutation from '@/__generated__/RegisterPageMutation.graphql.ts';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { PasswordField } from '@/features/auth/components/PasswordField';
import {
  captureEvent,
  createEventId,
  normalizeErrorCode,
} from '@/analytics/posthog';
import { trackMetaEvent } from '@/analytics/metaPixel';

const registerMutationGraphql = graphql`
  mutation RegisterPageMutation(
    $name: String!
    $email: String!
    $password: String!
    $company: String
  ) {
    register(name: $name, email: $email, password: $password, company: $company) {
      token
      user {
        id
        name
        email
      }
    }
  }
`;

const registerSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Please enter a valid email'),
    company: z.string().optional(),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

function normalizeRegisterError(message: string): string {
  if (message.includes('Email already registered')) {
    return 'An account with this email already exists.';
  }
  return 'We could not create your account. Please try again.';
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const invitedEmail = searchParams.get('email');
  const inviteWasAccepted = searchParams.get('inviteAccepted') === '1';
  const { login } = useAuth();
  const [commit, isInFlight] = useMutation(registerMutation);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: invitedEmail ?? '',
    },
  });

  const onSubmit = (values: RegisterFormValues) => {
    setError(null);
    const eventId = createEventId('signup');
    captureEvent('signup_started', { method: 'password', event_id: eventId });
    commit({
      variables: {
        name: values.name,
        email: values.email,
        password: values.password,
        company: values.company || null,
      },
      onCompleted: (response) => {
        if (response.register) {
          void login(response.register.token, response.register.user)
            .then(() => {
              captureEvent('signup_completed', {
                method: 'password',
                event_id: eventId,
                status: 'completed',
              });
              trackMetaEvent('CompleteRegistration', { status: 'completed' }, eventId);
              // The server now creates a default workspace + owner membership
              // as part of register, so the dashboard already has rows to
              // show. /onboarding/workspace remains reachable for users who
              // want to create an additional workspace via the sidebar.
              const next = inviteToken
                ? `/accept-invite?token=${encodeURIComponent(inviteToken)}${inviteWasAccepted ? '&resume=accepted' : ''}`
                : '/dashboard';
              navigate(next);
            })
            .catch(() => {
              captureEvent('signup_failed', {
                method: 'password',
                event_id: eventId,
                error_code: 'session_save_failed',
              });
              setError('Could not securely save your session. Please try again.');
            });
        }
      },
      onError: (err) => {
        captureEvent('signup_failed', {
          method: 'password',
          event_id: eventId,
          error_code: normalizeErrorCode(err.message ?? ''),
        });
        setError(normalizeRegisterError(err.message ?? ''));
      },
    });
  };

  return (
    <AuthLayout title="Create your account" subtitle="Start tracking your time in minutes.">
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-foreground">
            Full name
          </Label>
          <div className="relative">
            <UserRound
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="John Doe"
              aria-invalid={errors.name ? 'true' : 'false'}
              aria-describedby={errors.name ? 'register-name-error' : undefined}
              className="h-12 rounded-xl border-input bg-background pl-10 pr-4 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:ring-offset-0"
              {...register('name')}
            />
          </div>
          {errors.name && (
            <p id="register-name-error" className="text-xs text-destructive">
              {errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            Email
          </Label>
          <div className="relative">
            <Mail
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'register-email-error' : undefined}
              className="h-12 rounded-xl border-input bg-background pl-10 pr-4 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:ring-offset-0"
              readOnly={Boolean(inviteToken)}
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p id="register-email-error" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
          {inviteToken && !errors.email ? (
            <p className="text-xs text-muted-foreground">
              Use this invited email to connect your new account to the workspace.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-sm font-medium text-foreground">
            Company <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <div className="relative">
            <Building2
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="company"
              type="text"
              autoComplete="organization"
              placeholder="Acme Inc."
              aria-invalid={errors.company ? 'true' : 'false'}
              aria-describedby={errors.company ? 'register-company-error' : undefined}
              className="h-12 rounded-xl border-input bg-background pl-10 pr-4 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:ring-offset-0"
              {...register('company')}
            />
          </div>
          {errors.company && (
            <p id="register-company-error" className="text-xs text-destructive">
              {errors.company.message}
            </p>
          )}
        </div>

        <PasswordField
          id="password"
          label="Password"
          autoComplete="new-password"
          placeholder="Create a password"
          helperText="Use at least 6 characters."
          error={errors.password?.message}
          {...register('password')}
        />

        <PasswordField
          id="confirmPassword"
          label="Confirm password"
          autoComplete="new-password"
          placeholder="Confirm your password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <div aria-live="polite">
          {error && (
            <p
              role="alert"
              className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          className="h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
          disabled={isInFlight}
        >
          {isInFlight && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {isInFlight ? 'Creating account...' : 'Create account'}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Free plan available · No credit card required
        </p>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link
          to="/login"
          className="font-semibold text-primary transition-colors hover:text-primary-hover"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
